import { FC, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import Banner from "@src/lib/ui/Banner";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import RobotOutlineIcon from "@src/lib/icons/18px/robot-outline";
import XIcon from "@src/lib/icons/12px/x";
import { useBooleanLocalStorage } from "@src/lib/hooks/useLocalStorage";
import AgentGalleryDialog from "@src/features/agents/AgentGalleryDialog";
import AgentDialog from "@src/features/agents/AgentDialog";
import { useAgentPanel } from "@src/features/opus-agent/agentPanel";
import {
  OpusAgentFieldsFragment,
  OpusAgentsDocument,
  OpusAgentsQuery,
} from "@src/features/opus-agent/agentPanel.generated";

// What stays on the homepage once the release envelope has been opened: not
// the announcement again, but one small nudge to add a first agent. Only for
// orgs with agents on and none added yet.
const FirstAgentBanner: FC<{
  isPreview: boolean;
  // Takes focus when it replaces the envelope dialog, so keyboard users land
  // here instead of the page body.
  focusOnMount?: boolean;
}> = ({ isPreview, focusOnMount = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const client = useApolloClient();
  const { gates, agents, agentsLoading, openAgent } = useAgentPanel();
  const [dismissed, setDismissed] = useBooleanLocalStorage(
    "dismissed_first_agent_banner",
    false,
  );
  // Previews ignore stored dismissal, and dismissing one only hides it until
  // the next load.
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const hidden =
    (isPreview ? previewDismissed : dismissed) ||
    !gates.agents ||
    agentsLoading ||
    agents.length > 0;

  useEffect(() => {
    if (focusOnMount && !hidden) ref.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount
  }, []);

  // A new agent opens in the panel, as from the nav's Agents section.
  const openCreated = (agent: OpusAgentFieldsFragment) => {
    client.cache.updateQuery<OpusAgentsQuery>(
      { query: OpusAgentsDocument },
      (data) =>
        data && {
          OpusAgents: [
            ...data.OpusAgents.filter((existing) => existing.id !== agent.id),
            agent,
          ],
        },
    );
    openAgent(agent.id);
  };

  if (hidden && !galleryOpen && !creating) return null;

  return (
    <>
      {!hidden && (
        <div
          ref={ref}
          tabIndex={focusOnMount ? -1 : undefined}
          className="outline-none"
          data-testid="first-agent-banner"
        >
          <Banner
            variant="info"
            Icon={RobotOutlineIcon}
            action={
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setGalleryOpen(true)}
                >
                  Browse agents
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Dismiss"
                      onClick={() =>
                        isPreview
                          ? setPreviewDismissed(true)
                          : setDismissed(true)
                      }
                    >
                      <XIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Dismiss</TooltipContent>
                </Tooltip>
              </div>
            }
          >
            <span className="font-medium-v4">Add your first agent.</span> Pick
            one from the gallery and it gets to work for your team.
          </Banner>
        </div>
      )}
      {galleryOpen && (
        <AgentGalleryDialog
          open={galleryOpen}
          onOpenChange={setGalleryOpen}
          onCreated={openCreated}
          onCreateFromScratch={() => {
            setGalleryOpen(false);
            setCreating(true);
          }}
        />
      )}
      {creating && (
        <AgentDialog
          agent={null}
          onClose={() => setCreating(false)}
          onCreated={openCreated}
        />
      )}
    </>
  );
};

export default FirstAgentBanner;
